import {
  getInventory,
  getCompanyExperiments,
  clearSingleAmalgams,
  getExperiments
} from './Amalgam';
import { VIEW_TYPES } from '../molecules/OutOfStockBadge';
import I18n from './I18n';
import { getProduct, getProductSetQuantityMultiplier, isProductSet, isVariantSet } from './Product';
import { getGlobalMoqCount } from './Experiments';
import { isSearchResultView } from './Utils';

const variantInventoryCount = {};

export const getVariantInventoryCount = (productId) => variantInventoryCount[productId];
export const setVariantInventoryCount = (productId, count) =>
  (variantInventoryCount[productId] = count);
export const deleteVariantInventoryCount = (productId) => delete variantInventoryCount[productId];

export const isProductInStock = (productId, fromCustomPage) => {
  if (fromCustomPage) {
    const product = getProduct(productId, fromCustomPage);
    return product.productStockCount === null || product.productStockCount > 0;
  }
  const inventory = getInventory();

  return (
    !inventory[productId] ||
    typeof inventory[productId].count !== 'number' ||
    !(
      inventory[productId].count === 0 ||
      (getTrackInventoryConfig(productId) &&
        inventory[productId].count < (getMOQForProductId(productId) || 1))
    )
  );
};

const getTrackInventoryConfig = (productId) => {
  const inventory = getInventory();
  const experiments = getCompanyExperiments();

  if (inventory[productId] && typeof inventory[productId].trackInventory === 'boolean') {
    return inventory[productId].trackInventory;
  }

  return experiments.defaultExperiments.trackInventory;
};

export const getShowOutOfProductConfig = (productId) => {
  const inventory = getInventory();
  const experiments = isSearchResultView() ? getCompanyExperiments() : getExperiments();

  if (inventory[productId] && typeof inventory[productId].showOutOfStockProduct === 'boolean') {
    return inventory[productId].showOutOfStockProduct;
  }

  return experiments.showOutOfStockProduct;
};

export const getAllowOrdersOnOutOfStockConfig = (productId, viewType) => {
  if (viewType === VIEW_TYPES.SEARCH_GRID) {
    return getCompanyExperiments().allowOrdersOnOutOfStock;
  }

  const inventory = getInventory();
  const experiments = isSearchResultView() ? getCompanyExperiments() : getExperiments();

  if (inventory[productId] && typeof inventory[productId].allowOrdersOnOutOfStock === 'boolean') {
    return inventory[productId].allowOrdersOnOutOfStock;
  }

  return experiments.allowOrdersOnOutOfStock;
};

export const getOutOfStockLabelText = () => I18n.t('OUT_OF_STOCK');

export const shouldShowOutOfStockBadge = (productId, fromCustomPage) =>
  !isProductInStock(productId, fromCustomPage);

export const showOutOfStockHomeGrid = (productId, fromCustomPage, viewType) => {
  const product = getProduct(productId, fromCustomPage);
  if (!product) {
    return false;
  }

  if (!fromCustomPage && product.isVariant()) {
    const parentProduct = getProduct(product.parentProductId, fromCustomPage);
    const { availableVariants = [] } = parentProduct || {};
    // const isVariantInquiryAllowed = availableVariants.some((availableVariantId) => isInquiryAllowed(availableVariantId, viewType, fromCustomPage));
    const isVariantInquiryAllowed = availableVariants.some((availableVariantId) =>
      isProductInStock(availableVariantId, fromCustomPage)
    );
    return !isVariantInquiryAllowed;
  }

  return shouldShowOutOfStockBadge(productId, fromCustomPage);
};

export const inquiryAllowedOnAnyVariant = (productId, viewType, fromCustomPage) => {
  if (viewType === VIEW_TYPES.SEARCH_GRID) {
    return isInquiryAllowed(productId, viewType);
  }

  const product = getProduct(productId, fromCustomPage);
  if (!product) {
    return false;
  }

  if (product.isVariant()) {
    const parentProduct = getProduct(product.parentProductId, fromCustomPage);
    const { availableVariants = [] } = parentProduct || {};
    return availableVariants.some((id) => isInquiryAllowed(id, fromCustomPage));
  }

  return isInquiryAllowed(productId, viewType, fromCustomPage);
};

export const isInquiryAllowed = (productId, viewType, fromCustomPage) => {
  const allowOrdersOnOutOfStockConfig = getAllowOrdersOnOutOfStockConfig(productId, viewType);
  const productInStock = isProductInStock(productId, fromCustomPage);
  return allowOrdersOnOutOfStockConfig || productInStock;
};

export const getMaximumItemCount = (productId) => {
  const inventory = getInventory();

  if (!inventory[productId] || typeof inventory[productId].count !== 'number') {
    if (getTrackInventoryConfig(productId)) {
      if (getShowOutOfProductConfig(productId) && getAllowOrdersOnOutOfStockConfig(productId)) {
        return Number.MAX_SAFE_INTEGER;
      }

      return 1;
    }

    return Number.MAX_SAFE_INTEGER;
  }

  if (inventory[productId].count === 0) {
    if (getShowOutOfProductConfig(productId) && getAllowOrdersOnOutOfStockConfig(productId)) {
      return Number.MAX_SAFE_INTEGER;
    }

    return 0;
  }

  if (!getTrackInventoryConfig(productId)) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (getShowOutOfProductConfig(productId) && getAllowOrdersOnOutOfStockConfig(productId)) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (isProductSet({ productId }) || isVariantSet({ productId })) {
    const multiplier = getProductSetQuantityMultiplier({ productId }) || 1;
    return multiplier * inventory[productId].count;
  }

  return inventory[productId].count;
};

export const shouldShowProduct = (productId) =>
  getShowOutOfProductConfig(productId) || isProductInStock(productId);

export const refreshInventory = () => {
  clearSingleAmalgams();
};

export const getProductInventory = (productId) => {
  const inventory = getInventory();

  return inventory && inventory[productId] ? inventory[productId] : {};
};

export const getMOQForProductId = (productId) => {
  const inventory = getInventory();
  if (inventory && inventory[productId] && inventory[productId].minOrderQuantity) {
    return inventory[productId].minOrderQuantity;
  }

  return getGlobalMoqCount() || null;
};

export const showMoqField = (productId) => {
  // Moq field will be shown if the product or variant is a set
  if (isProductSet({ productId }) || isVariantSet({ productId })) {
    return true;
  }

  const moq = getMOQForProductId(productId);
  if (typeof moq !== 'number' || moq <= 1) {
    return false;
  }

  return true;
};

export const getProductOrVariantInventoryCount = (productId) => {
  let quantityCount = getVariantInventoryCount(productId);

  if (typeof quantityCount !== 'number') {
    quantityCount = getMOQForProductId(productId) || 1;
  }

  return quantityCount || 1;
};
